import { queueMacrotask } from 'module/helpers/macrotask';
import {
  DefaultCustomLayoutDrawerProps,
  Drawer,
} from 'module/ui/components/atoms/drawer';
import { Form } from 'module/ui/components/atoms/form';
import { useState, useEffect, FC, useCallback } from 'react';
import { AddKnowledgeTriggerType } from '../add-knowledge-trigger';
import { FolderContentCreateStep } from './folder-content-create-step';
import { FolderCreateStep, FolderStepFormValues } from './folder-create-step';
import { AiPagesPayload } from '../add-ai-pages/ai-pages';
import { AddFilesPayload } from '../add-files';
import { AddWebsitePayload } from '../add-website';

enum Step {
  FolderCreate = 'folder-create',
  FolderContent = 'folder-content',
}

export type CreateKnowledgeBaseFolderPayload = {
  folderName: string;
  contentType: AddKnowledgeTriggerType;
  contentPayload: AiPagesPayload | AddWebsitePayload | AddFilesPayload;
};

interface Props {
  open: boolean;
  onClose: () => void;
  onDone: (payload: {
    folderName: string;
    contentType: AddKnowledgeTriggerType;
    contentPayload: AiPagesPayload | AddWebsitePayload | AddFilesPayload;
  }) => Promise<void>;
}

export const CreateKnowledgeBaseFolderDrawer: FC<Props> = ({
  open,
  onClose,
  onDone,
}) => {
  const [folderStepForm] = Form.useForm<FolderStepFormValues>();
  const [actionLoading, setActionLoading] = useState(false);
  const [folderStepFormValid, setFolderStepFormValid] = useState(false);
  const [currentStep, setCurrentStep] = useState<Step>(Step.FolderCreate);

  useEffect(() => {
    if (open) {
      folderStepForm.resetFields();
      setFolderStepFormValid(false);
      setActionLoading(false);
      setCurrentStep(Step.FolderCreate);
    }
  }, [open, folderStepForm]);

  const onFolderStepFormValuesChange = useCallback(() => {
    queueMacrotask(() => {
      const folderName =
        folderStepForm.getFieldValue('folderName')?.trim() ?? '';

      setFolderStepFormValid(folderName.length > 0);
    });
  }, [folderStepForm]);

  const onFolderCreateComplete = useCallback(() => {
    folderStepForm
      .validateFields()
      .then(() => {
        setCurrentStep(Step.FolderContent);
      })
      .catch(() => {});
  }, [folderStepForm]);
  const onFolderContentCreateComplete = useCallback(
    async (payload: AiPagesPayload | AddWebsitePayload | AddFilesPayload) => {
      setActionLoading(true);
      await onDone({
        folderName: folderStepForm.getFieldValue('folderName')?.trim() ?? '',
        contentType: folderStepForm.getFieldValue('contentType'),
        contentPayload: payload,
      });
      setActionLoading(false);
    },
    [folderStepForm, onDone],
  );

  return (
    <Drawer
      width="672px"
      open={open}
      onClose={onClose}
      {...DefaultCustomLayoutDrawerProps}
    >
      {currentStep === Step.FolderCreate && (
        <FolderCreateStep
          form={folderStepForm}
          onFormValuesChange={onFolderStepFormValuesChange}
          onClose={onClose}
          onNext={onFolderCreateComplete}
          isFormValid={folderStepFormValid}
        />
      )}
      {currentStep === Step.FolderContent && (
        <FolderContentCreateStep
          folderCreateStepForm={folderStepForm}
          onClose={onClose}
          onNext={onFolderContentCreateComplete}
          actionLoading={actionLoading}
        />
      )}
    </Drawer>
  );
};
